import React, { useEffect, useState } from 'react';
import { GlobalStyles } from '@mui/material';
import { styled } from '@mui/material/styles';
import { NavLink } from 'react-router-dom';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import LinkIcon from '@mui/icons-material/Link';
import AssignmentIcon from '@mui/icons-material/Assignment';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import HomeIcon from '@mui/icons-material/Home';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import SettingsIcon from '@mui/icons-material/Settings';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import BarChartIcon from '@mui/icons-material/BarChart';
import PersonIcon from '@mui/icons-material/Person';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import FormatListNumberedRtlIcon from '@mui/icons-material/FormatListNumberedRtl';
import WcIcon from '@mui/icons-material/Wc';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import PostAddIcon from '@mui/icons-material/PostAdd';
import PregnantWomanIcon from '@mui/icons-material/PregnantWoman';
import ChatIcon from '@mui/icons-material/Chat';
import { ThemeContext, SnackContext } from 'src/store/ContextStore';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import axios from 'src/axios';
import { APICONSTANTS } from 'src/components/Constants';
import { SchemaRounded } from '@mui/icons-material';
import DNAIcon from '../CustomIcons/DNAIcon';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import CodeIcon from '@mui/icons-material/Code';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';

// Width of the desktop icon rail (px). Gives the icons + full-width highlight
// comfortable breathing room.
const SIDEBAR_WIDTH = 72;

const ListIconStyled = styled('div')(({ appearance, sidebarText }) => ({
  color:
    appearance.value === 'light'
      ? sidebarText ?? '#000'
      : sidebarText ?? '#fff',
  minWidth: 40,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
}));

function CustomNavIcon(props) {
  const { sidebarText, appearance, icon } = props;
  let iconComp = '';
  switch (icon) {
    case 'home':
      iconComp = <HomeIcon />;
      break;
    case 'generate-link':
      iconComp = <FamilyRestroomIcon />;
      break;
    case 'families':
      iconComp = <PersonSearchIcon />;
      break;
    case 'api':
      iconComp = <CodeIcon />;
      break;
    case 'pedigree-tool':
      iconComp = <AccountTreeIcon />;
      break;
    case 'pedigree-tool-v2':
      iconComp = <SchemaRounded />;
      break;
    case 'settings':
      iconComp = <SettingsIcon />;
      break;
    case 'desktop-migration':
      iconComp = <CloudSyncIcon />;
      break;
    case 'add-users':
      iconComp = <PersonAddIcon />;
      break;
    case 'person':
      iconComp = <PersonIcon />;
      break;
    case 'people':
      iconComp = <PeopleAltIcon />;
      break;
    case 'pregnant':
      iconComp = <PregnantWomanIcon />;
      break;
    case 'numbered-list':
      iconComp = <FormatListNumberedRtlIcon />;
      break;
    case 'wc':
      iconComp = <WcIcon />;
      break;
    case 'list-check':
      iconComp = <PlaylistAddCheckIcon />;
      break;
    case 'post-add':
      iconComp = <PostAddIcon />;
      break;
    case 'assignment':
      iconComp = <AssignmentIcon />;
      break;
    case 'chat':
      iconComp = <ChatIcon />;
      break;
    case 'carrier-screen':
      iconComp = <DNAIcon />;
      break;
    case 'data-explorer':
      iconComp = <BarChartIcon />;
      break;
    case 'pathways':
      iconComp = <AltRouteIcon />;
      break;
    case 'clinic-management':
      iconComp = <EventAvailableOutlinedIcon />;
      break;
    case 'lab-report-inbox':
      iconComp = <MoveToInboxIcon />;
      break;
    default:
      return '';
  }

  return (
    <ListIconStyled appearance={appearance} sidebarText={sidebarText}>
      {iconComp}
    </ListIconStyled>
  );
}

/* ── Desktop sidebar (lg and up) ── */

const DrawerContainer = styled(List)(({ theme, sidebar, appearance }) => ({
  display: 'flex',
  flexDirection: 'column',
  paddingTop: 64,
  backgroundColor: sidebar ?? appearance.color,
  width: '100%',
  transition: `background-color 1s, color 1s`,
  alignItems: 'center'
}));

const DrawerClose = styled('div')(({ theme, sidebar, appearance }) => ({
  height: '100vh',
  '& > div': {
    overflow: 'hidden'
  },
  borderRight: `.5px solid #c1c1c170`,
  backgroundColor: sidebar ?? appearance.color,
  transition: `${theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  })}, background-color 1s, color 1s`,
  whiteSpace: 'nowrap',
  width: SIDEBAR_WIDTH
}));

// Passive full-width container that centres the icon. The hover / selected
// highlight (tint + left accent bar) lives on the StyledNavLink itself so it
// spans the full width of the rail. Also serves as the ref-forwarding child
// the Tooltip requires.
const ListItemStyled = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  // Vertical padding (not margin) spaces the icons out while keeping the
  // highlight blocks flush — so a hover highlight abuts the selected highlight
  // of the icon above with no gap.
  padding: '12px 0'
});

const Version = styled(ListItem)(({ theme }) => ({
  position: 'fixed',
  bottom: '1rem',
  width: 'auto'
}));

const StyledNavLink = styled(NavLink, {
  shouldForwardProp: (prop) => prop !== 'accent'
})(({ accent = '#2ba9bf' }) => ({
  textDecoration: 'none !important',
  color: '#000',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  position: 'relative',
  overflow: 'hidden',
  transition: 'background-color 150ms ease',
  // Subtle accent tint on hover; the selected item gets a (slightly lighter)
  // accent tint PLUS a left accent indicator bar. The bar — not a darker fill —
  // is what signals selection. All colours derive from the theme `accent` token.
  '&:hover': {
    backgroundColor: `${accent}18` // accent at ~9%
  },
  '&.active': {
    backgroundColor: `${accent}14`, // accent at ~8%
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: '50%',
      transform: 'translateY(-50%)',
      width: 3,
      height: '62%',
      backgroundColor: accent,
      borderRadius: '0 3px 3px 0'
    }
  }
}));

/* ── Mobile menu item ── */

const MobileNavItem = styled(NavLink, {
  shouldForwardProp: (prop) => prop !== 'accent'
})(({ accent = '#2ba9bf' }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 14,
  padding: '13px 20px',
  textDecoration: 'none !important',
  color: 'inherit',
  borderLeft: '3px solid transparent',
  transition: 'background-color 150ms ease, border-color 150ms ease',
  '&:hover': {
    backgroundColor: `${accent}12` // accent at ~7%
  },
  '&.active': {
    backgroundColor: `${accent}10`, // accent at ~6%
    borderLeftColor: accent // left accent bar
  }
}));

export default function CustomDrawer({
  navItems = [],
  mobileNavOpen = false,
  onMobileNavClose
}) {
  const {
    theme: { appearance, accent, sidebar, sidebarText }
  } = React.useContext(ThemeContext);
  const [currentVersion, setCurrentVersion] = useState('');

  const t = useAutoTranslation();

  const getCurrentVersion = () => {
    axios
      .get(APICONSTANTS.getCurrentVersion)
      .then(({ data }) => {
        if (data.current_version) setCurrentVersion(data.current_version);
      })
      .catch(() => {});
  };

  useEffect(() => {
    getCurrentVersion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {/* Global Styles */}
      <GlobalStyles
        styles={{
          // Recolour the selected icon to the accent colour. The soft selected
          // background tint + the left accent indicator bar are applied on the
          // NavLink itself (StyledNavLink). Inactive/hover icons keep their
          // normal colour — hover only tints the background.
          'a.drawer-item.active > div > div': {
            color: accent
          }
        }}
      />
      {/* ── Desktop sidebar (lg+) ── */}
      <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
        <DrawerClose sidebar={sidebar} appearance={appearance}>
          <div style={{ padding: 0.5, background: '#c1c1c170' }} />
          <DrawerContainer
            component="div"
            disablePadding
            sidebar={sidebar}
            appearance={appearance}
          >
            {navItems.map((item) => (
              <StyledNavLink
                key={item.icon}
                to={item.link ?? item.path}
                id={item?.id}
                data-va-anchor={`nav.${item.icon}`}
                className="drawer-item"
                accent={accent}
              >
                <Tooltip title={t(item.label)} placement="right">
                  <ListItemStyled>
                    {item.icon && (
                      <CustomNavIcon
                        appearance={appearance}
                        sidebarText={sidebarText}
                        icon={item.icon}
                      />
                    )}
                  </ListItemStyled>
                </Tooltip>
              </StyledNavLink>
            ))}
            <Version>
              <Typography
                variant="caption"
                gutterBottom
                sx={{
                  color: 'inherit',
                  display: 'block'
                }}
              >
                v{currentVersion}
              </Typography>
            </Version>
          </DrawerContainer>
        </DrawerClose>
      </Box>
      {/* ── Mobile dropdown menu (below lg) ── */}
      <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
        <Collapse in={mobileNavOpen}>
          <Box
            sx={{
              position: 'fixed',
              top: 64,
              left: 0,
              right: 0,
              zIndex: 1200,
              bgcolor: sidebar ?? appearance.color,
              borderBottom: '1px solid #c1c1c170',
              maxHeight: 'calc(100vh - 64px)',
              overflowY: 'auto',
              boxShadow: 3
            }}
          >
            {navItems.map((item) => (
              <MobileNavItem
                key={item.icon}
                to={item.link ?? item.path}
                accent={accent}
                className="drawer-item"
                onClick={onMobileNavClose}
              >
                <CustomNavIcon
                  appearance={appearance}
                  sidebarText={sidebarText}
                  icon={item.icon}
                />
                <Typography
                  variant="body2"
                  sx={{
                    color:
                      appearance.value === 'light'
                        ? sidebarText ?? '#000'
                        : sidebarText ?? '#fff',
                    fontWeight: 500
                  }}
                >
                  {t(item.label)}
                </Typography>
              </MobileNavItem>
            ))}
            {currentVersion && (
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  textAlign: 'right',
                  px: 2,
                  py: 1,
                  color: 'text.secondary'
                }}
              >
                v{currentVersion}
              </Typography>
            )}
          </Box>
        </Collapse>
      </Box>
    </>
  );
}
